import { Component } from 'react';
import { Subscription } from 'rxjs';

import { FormControl, FormGroup } from '../../../models/forms';
import styles from './word-count.component.module.scss';
import sharedStyles from '../single-page.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import { PersistedInput } from '../../../utils/persisted-input';

const STORAGE_KEY = 'word-count';

interface Stats {
    words: number;
    chars: number;
    charsNoSpaces: number;
    sentences: number;
    paragraphs: number;
    readingMinutes: number;
}

function computeStats(text: string): Stats {
    const t = text || '';
    const words = t.trim() ? t.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean).length : 0;
    const chars = t.length;
    const charsNoSpaces = t.replace(/\s/g, '').length;
    // A "sentence" ends with .!? possibly followed by quotes / brackets.
    const sentences = t.trim() ? (t.match(/[^.!?\n]+[.!?]+/g) || []).length || (t.trim() ? 1 : 0) : 0;
    // Paragraphs separated by blank lines (one or more).
    const paragraphs = t.trim() ? t.split(/\n\s*\n/).filter(p => p.trim()).length : 0;
    // Average adult reading speed ~225 wpm.
    const readingMinutes = words > 0 ? Math.max(1, Math.ceil(words / 225)) : 0;
    return { words, chars, charsNoSpaces, sentences, paragraphs, readingMinutes };
}

export class WordCountComponent extends Component<{}, { stats: Stats }> {
    form: FormGroup;
    private sub: Subscription | null = null;
    private saveTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(props: any) {
        super(props);
        const restored = PersistedInput.load(STORAGE_KEY, { input: '' },
            (v): v is { input: string } => !!v && typeof (v as any).input === 'string');
        this.form = new FormGroup({
            textinput: new FormControl(restored.input),
        });
        this.state = { stats: computeStats(restored.input) };
    }

    componentDidMount() {
        this.sub = this.form.get('textinput').valueChanges.subscribe(
            (data: string) => {
                this.setState({ stats: computeStats(data || '') });
                if (this.saveTimer) clearTimeout(this.saveTimer);
                this.saveTimer = setTimeout(() => PersistedInput.save(STORAGE_KEY, { input: data || '' }), 500);
            },
            (err) => console.error(err),
        );
    }

    componentWillUnmount() {
        this.sub?.unsubscribe();
        if (this.saveTimer) clearTimeout(this.saveTimer);
    }

    render() {
        const { stats } = this.state;
        return (<FullscreenHost><div className="row">
            <div className="col-12">
                <div className={sharedStyles.card} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '1rem 1.15rem', marginBottom: '1rem' }}>
                    <textarea className={sharedStyles.textarea}
                        placeholder="Start typing or paste text here..."
                        value={this.form.controls['textinput'].value}
                        onInput={(e: any) => this.form.controls['textinput'].setValue(e.target.value)}
                        rows={12}></textarea>
                </div>
            </div>

            <div className="col-12">
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}><span className={styles.statValue}>{stats.words.toLocaleString()}</span><span className={styles.statLabel}>Words</span></div>
                    <div className={styles.statCard}><span className={styles.statValue}>{stats.chars.toLocaleString()}</span><span className={styles.statLabel}>Characters</span></div>
                    <div className={styles.statCard}><span className={styles.statValue}>{stats.charsNoSpaces.toLocaleString()}</span><span className={styles.statLabel}>Chars (no spaces)</span></div>
                    <div className={styles.statCard}><span className={styles.statValue}>{stats.sentences.toLocaleString()}</span><span className={styles.statLabel}>Sentences</span></div>
                    <div className={styles.statCard}><span className={styles.statValue}>{stats.paragraphs.toLocaleString()}</span><span className={styles.statLabel}>Paragraphs</span></div>
                    <div className={styles.statCard}><span className={styles.statValue}>{stats.readingMinutes}<span style={{ fontSize: '0.7em', color: '#64748b' }}> min</span></span><span className={styles.statLabel}>Reading time</span></div>
                </div>
            </div>
        </div></FullscreenHost>);
    }
}
